import * as React from 'react';
import { connect } from 'react-redux';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { RootState } from '../../../store';
import {
    OptimizeData,
    TreeDisplayStateType,
    NodeEditData,
    GeofenceEditData,
} from '../../../store/FMTree/FMTreeReducer';
import {
  replaceInfoPanelOptimizeMode,
  optimizeGeofence,
  replaceOptimizeMinDistanceBetweenPoints,
  replaceOptimizeAllowedDeviationFromLine,
  updateArea,
  saveOptimizedGeofence,
} from '../../../store/FMTree/FMTreeActions';
import {
    getOptimizedData,
    getNodeEditData,
} from '../../../store/FMTree/FMTReeSelectors';
import {
  FMTextInput,
  FMButton,
  FMContainer,
  FMTable,
  FMTableHead,
  FMTableBody,
  FMTableRow,
  FMTableCell,
  FMIconButton,
} from '../../atoms';
import { FMLoading } from 'molecules';
import style from './GeotreeInfoPanel.style';

interface StateProps {
  data: OptimizeData;
  editData: NodeEditData;
}

interface DispatchProps {
  replaceInfoPanelOptimizeMode: (displayStateType: string, modeEnabled: boolean) => void;
  optimizeGeofence: any;
  replaceOptimizeMinDistanceBetweenPoints: any;
  replaceOptimizeAllowedDeviationFromLine: any;
  updateArea: any;
  saveOptimizedGeofence: any;
}

type Props = StateProps & DispatchProps & WithStyles;

const OptimizePanel = (props: Props) => {
    const {
        minDistanceBetweenPoints,
        allowedDeviationFromLine,
        optimizationComplete,
        currentPointsCount,
        currentArea,
        newArea,
        isFetching,
    } = props.data;
    const { classes, editData } = props;
    const translate = fmtools.translate;
    const closeOptimizePanel = (): void => {
        props.replaceInfoPanelOptimizeMode(
            TreeDisplayStateType.mainGeofencesTree,
            false,
        );
    };
    const makeOptimization = (): void => {
        props.optimizeGeofence();
    };
    const changeMinDistance = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        let newValue = parseInt(event.target.value, 10);
        if (isNaN(newValue)) {
            newValue = 0;
        }
        props.replaceOptimizeMinDistanceBetweenPoints(newValue);
    };
    const changeAllowedDeviation = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      let newValue = parseInt(event.target.value, 10);
      if (isNaN(newValue)) {
          newValue = 0;
      }
      props.replaceOptimizeAllowedDeviationFromLine(newValue);
    };
    const editGeoData = editData as GeofenceEditData;
    const updateAreaFunc = (): void => {
        props.updateArea(editGeoData.points);
    };
    const saveOptimizedPoints = (): void => {
        props.saveOptimizedGeofence(editGeoData.points);
        closeOptimizePanel();
    };

    const minDistLabel = `${translate('MinDistanceBetweenPoints')}, ${translate('meter')}:`;
    const deviationLabel = `${translate('MinDeviationAngle')}, °:`;
    let pointsCompressRateCalculated = 0;
    let compressionString = 0;
    if (editData) {
        pointsCompressRateCalculated = currentPointsCount / editGeoData.points.length;
        compressionString = parseFloat(pointsCompressRateCalculated.toFixed(2));
    }
        
    return (
        <>
            {
                isFetching &&
                <FMLoading />
            }
            {
                !isFetching &&
                <FMContainer className={classes.panel}>
                    <div className={classes.optimizationHeader}>
                        <Typography>{translate('OptimizationParameters')}</Typography>
                    </div>
                    <div className={classes.optimizeParamContainer}>
                        <Typography className={classes.optimizeParamLabel}>{minDistLabel}</Typography>
                        <FMTextInput
                            className={classes.optimizeParamInput}
                            value={minDistanceBetweenPoints}
                            onChange={changeMinDistance}
                        />
                    </div>
                    <div className={classes.optimizeParamContainer}>
                        <Typography className={classes.optimizeParamLabel}>{deviationLabel}</Typography>
                        <FMTextInput
                            className={classes.optimizeParamInput}
                            value={allowedDeviationFromLine}
                            onChange={changeAllowedDeviation}
                        />
                    </div>
                    <div className={classes.optimizeButtonContainer}>
                        <FMButton
                            variant="outlined"
                            color="primary"
                            onClick={makeOptimization}
                        >
                            {translate('Optimize')}
                        </FMButton>
                    {
                        !optimizationComplete &&
                        <FMButton
                            variant="outlined"
                            color="primary"
                            onClick={closeOptimizePanel}
                        >
                            {translate('Cancel')}
                        </FMButton>
                    }
                    </div>
                    {
                        optimizationComplete &&
                        <>
                            <div className={classes.optimizationHeader}>
                                <Typography>{translate('OptimizationResult')}</Typography>
                            </div>
                            <div className={classes.optimizeTableContainer}>
                                <FMTable>
                                    <FMTableHead>
                                        <FMTableRow>
                                            <FMTableCell>{translate('Parameter')}</FMTableCell>
                                            <FMTableCell>{translate('Before')}</FMTableCell>
                                            <FMTableCell>{translate('After')}</FMTableCell>
                                        </FMTableRow>
                                    </FMTableHead>
                                    <FMTableBody>
                                        <FMTableRow>
                                            <FMTableCell><Typography>{translate('Points')}</Typography></FMTableCell>
                                            <FMTableCell><Typography>{currentPointsCount}</Typography></FMTableCell>
                                            <FMTableCell><Typography>{editGeoData.points.length}</Typography></FMTableCell>
                                        </FMTableRow>
                                        <FMTableRow>
                                            <FMTableCell><Typography>
                                                {`${translate('Area')}, ${translate('hectare')}`}
                                            </Typography></FMTableCell>
                                            <FMTableCell><Typography>{currentArea}</Typography></FMTableCell>
                                            {
                                                !editGeoData.hasChanged &&
                                                <FMTableCell><Typography>{newArea}</Typography></FMTableCell>
                                            }
                                            {
                                                editGeoData.hasChanged &&
                                                <FMTableCell><FMIconButton type="refresh" style={{ width: '24px', height: '24px' }} onClick={updateAreaFunc} /></FMTableCell>
                                            }
                                        </FMTableRow>
                                    </FMTableBody>
                                </FMTable>
                            </div>
                            <div className={classes.optimizeText}>
                                <Typography>{`${translate('PointsCompressionRate')}: ${compressionString}`}</Typography>
                            </div>
                            <div className={classes.optimizeText}>
                                {
                                    !editGeoData.hasChanged &&
                                    <Typography>{`${translate('AreaDeviationValue')}: ${parseFloat(((1 - newArea / currentArea) * 100).toFixed(3))}%`}</Typography>
                                }
                                {
                                    editGeoData.hasChanged &&
                                    <Typography>
                                        {`${translate('AreaDeviationValue')}: `}
                                        <FMIconButton type="refresh" style={{ width: '24px', height: '24px' }} onClick={updateAreaFunc} />
                                    </Typography>
                                }
                            </div>
                            <div className={classes.optimizeButtonContainer}>
                                <FMButton
                                    variant="outlined"
                                    color="primary"
                                    onClick={saveOptimizedPoints}
                                >
                                    {translate('Save')}
                                </FMButton>
                                <FMButton
                                    variant="outlined"
                                    color="primary"
                                    onClick={closeOptimizePanel}
                                >
                                    {translate('Cancel')}
                                </FMButton>
                            </div>
                        </>
                    }
                </FMContainer>
            }
        </>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    data: getOptimizedData(state, TreeDisplayStateType.mainGeofencesTree),
    editData: getNodeEditData(state, TreeDisplayStateType.mainGeofencesTree),
});

const mapDispatchToProps = {
  replaceInfoPanelOptimizeMode,
  optimizeGeofence,
  replaceOptimizeMinDistanceBetweenPoints,
  replaceOptimizeAllowedDeviationFromLine,
  updateArea,
  saveOptimizedGeofence,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(style)(OptimizePanel));
